// Job status indicator component

import type { JobStatus as JobStatusType } from "@dishsnap/shared";
import styles from "./JobStatus.module.css";

interface JobStatusProps {
  status: JobStatusType;
  error?: string | null;
}

const STATUS_CONFIG: Record<JobStatusType, { label: string; step: number }> = {
  queued: { label: "Waiting to start", step: 0 },
  resolving: { label: "Resolving video URL", step: 1 },
  encoding: { label: "Processing video", step: 2 },
  scoring: { label: "Analyzing frames", step: 3 },
  done: { label: "Complete", step: 4 },
  failed: { label: "Failed", step: -1 },
};

const STEPS = [
  "Queued",
  "Resolving",
  "Processing",
  "Analyzing",
  "Complete",
];

export function JobStatus({ status, error }: JobStatusProps) {
  const config = STATUS_CONFIG[status];
  const isFailed = status === "failed";
  const isComplete = status === "done";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={`${styles.statusBadge} ${styles[status]}`}>
          {config.label}
        </span>
      </div>

      {!isFailed && (
        <div className={styles.steps}>
          {STEPS.map((stepLabel, index) => {
            const isActive = index === config.step;
            const isPast = index < config.step;
            const isFuture = index > config.step;

            return (
              <div
                key={stepLabel}
                className={`${styles.step} ${isActive ? styles.active : ""} ${isPast ? styles.past : ""} ${isFuture ? styles.future : ""}`}
              >
                <div className={styles.stepDot}>
                  {isPast || isComplete ? (
                    <svg viewBox="0 0 16 16" fill="currentColor" className={styles.checkIcon}>
                      <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                    </svg>
                  ) : isActive ? (
                    <span className={styles.activeDot} />
                  ) : null}
                </div>
                <span className={styles.stepLabel}>{stepLabel}</span>
              </div>
            );
          })}
        </div>
      )}

      {isFailed && error && (
        <div className={styles.errorBox}>
          <p className={styles.errorText}>{formatError(error)}</p>
        </div>
      )}
    </div>
  );
}

function formatError(error: string): string {
  // Extract human-readable message from error codes
  const errorMap: Record<string, string> = {
    invalid_url: "The URL provided is not a valid TikTok video link.",
    video_unavailable: "This video is unavailable. It may be private, deleted, or region-locked.",
    resolver_failed: "Could not access the video. The service may be temporarily unavailable.",
    encoding_timeout: "Video processing took too long. Please try again.",
    video_too_long: "This video exceeds the 10-minute limit.",
    no_dish_found: "Could not identify a clear finished dish in this video.",
    scoring_failed: "Failed to analyze video frames. Please try again.",
    rate_limited: "Too many requests. Please wait before trying again.",
    internal_error: "An unexpected error occurred. Please try again.",
  };

  for (const [code, message] of Object.entries(errorMap)) {
    if (error.includes(code)) {
      return message;
    }
  }

  return error;
}
